import { cloneDeep, orderBy, uniq } from 'lodash';
import flatten from 'lodash/flatten';

import { TransitionType } from 'SharedDir/constants/route';
import { NIssue } from 'SharedDir/declarations/issue';
import { RouteError } from 'SharedDir/service/issues/issue.exceptions';

/**
 * Проверяем все ли переходы "вперед" из параллельных шагов ведут к одному этапу
 *
 * Не учитываются переходы типа _revision_ (потому что нам на них все равно)
 * и _fallback_ (потому что их не должно существовать из параллельных состояний)
 *
 * @throws {RouteError}
 * @param transitions
 */
export const checkCommonChild = (transitions: Array<NIssue.FixedTransition>): void => {
  const filtered = transitions.filter((t) => t.type === TransitionType.approve && !t.toDeclineStep);
  const childIds = filtered.map(({ toStepId }) => toStepId);
  if (uniq(childIds).length > 1) {
    throw new RouteError(
      `Transitions from parallel steps lead to multiple destinations ${uniq(childIds)}`
    );
  }
};

export default class SuperStep implements NIssue.StepSuperposition {
  head: NIssue.StepWithMap;

  /**
   * Этапы в которые существуют пераллельные перходы вместе переходами из них
   *
   * Массив будет пустой если параллельных переходов нет
   *
   * @type {Array<NIssue.StepWithMap>}
   * @memberof SuperStep
   */
  branches: Array<NIssue.StepWithMap>;

  constructor(head: NIssue.StepWithMap, branches: Array<NIssue.StepWithMap> = []) {
    // Так же на всякий случай проверяем все
    // ли преходы вперед из параллельных
    // этапов ведут в одинаковый  этап
    checkCommonChild(flatten(branches.map(({ outgoingTransitions }) => outgoingTransitions)));

    this.head = head;
    this.branches = branches;
  }

  get isFinal(): boolean {
    return this.head.isFinal;
  }

  get isDecline(): boolean {
    return this.head.isDecline;
  }

  get isAllowCancelByIssuer(): boolean {
    return this.head.isAllowCancelByIssuer ?? false;
  }

  get isAllowIssuerViewValues(): boolean {
    return this.head.isAllowIssuerViewValues ?? false;
  }

  get isAllowFillByUser(): boolean {
    return this.head.isAllowFillByUser;
  }

  get isBranchPoint(): boolean {
    return this.branches.length > 0;
  }

  get name(): string {
    return this.head.name;
  }

  get id(): number {
    return this.head.id;
  }

  /**
   * id следующего этапа на основании переходов из "головы"
   * или параллельных переходов из "ветвей".
   *
   * Учитываются только переходы типа _approve_
   *
   * Переходов у параллельных состояний может быть несколько.
   *
   * @readonly
   * @type {(null | number)}
   * @memberof SuperStep
   */
  get nextStepId(): null | number {
    return this.superpositionForward?.toStepId || null;
  }

  /**
   * Переход в шаг-отмену, не может быть у шагов идущих параллельно
   *
   * @readonly
   * @type {(null | NIssue.FixedTransition)}
   * @memberof SuperStep
   */
  get decline(): null | NIssue.FixedTransition {
    return this.head.outgoingTransitions.find(({ toDeclineStep }) => toDeclineStep) || null;
  }

  /**
   * Переход из шага на доработку
   *
   * @readonly
   * @type {(null | NIssue.FixedTransition)}
   * @memberof SuperStep
   */
  get fallback(): null | NIssue.FixedTransition {
    return (
      this.head.outgoingTransitions.find(({ type }) => type === TransitionType.fallback) || null
    );
  }

  /**
   * Перевод из текущего шага на доработку
   * Проверка проводится всегда по своим возможным этапам перехода
   *
   *  @readonly
   * @type {(null | NIssue.FixedTransition)}
   * @memberof SuperStep
   */
  get revision(): null | NIssue.FixedTransition {
    let transitions: Array<NIssue.FixedTransition>;
    transitions = this.head.outgoingTransitions;
    console.log(transitions);

    return transitions.find(({ type }) => type === TransitionType.revision) || null;
  }

  /**
   * Переходна "вперед"(approve) следующий этап
   * Если это простой этап то ищется в его собственных переходах
   * Если это суперпозиция то первый переход "вперед" из любого из параллельных этапов
   *
   * @readonly
   * @type {(NIssue.FixedTransition | null)}
   * @memberof SuperStep
   */
  get superpositionForward(): NIssue.FixedTransition | null {
    let transitions: Array<NIssue.FixedTransition>;
    if (this.isBranchPoint) {
      transitions = this.allBranchesTransitions;
    } else {
      transitions = this.head.outgoingTransitions;
    }
    return transitions.find((t) => t.type === TransitionType.approve && !t.toDeclineStep) || null;
  }

  /**
   * Переходы "вперед"(approve и не в шаг отмену)
   * Если из шага существуют параллельные переходы
   * то будут возвращен первый найденный
   *
   * @readonly
   * @type {null | NIssue.FixedTransition }
   * @memberof SuperStep
   */
  get forward(): null | NIssue.FixedTransition {
    return (
      this.head.outgoingTransitions.find(
        (t) => t.type === TransitionType.approve && !t.toDeclineStep
      ) || null
    );
  }

  /**
   * Проверяем является ли шаг текущим для заявления
   *
   * @param {NIssue.IssueWithHistory} issue
   * @param {NIssue.Step} firstStep - первый шаг нужен для того что бы учитывать историю только до
   *  первого шага если было возвращение на доработку
   * @returns {boolean}
   * @memberof SuperStep
   */
  public checkIfStepIsCurrentForIssue(
    issue: NIssue.IssueWithHistory,
    firstStep: NIssue.Step
  ): boolean {
    const descendingLog = cloneDeep(
      orderBy(issue.stepChanges, [(stepChange) => new Date(stepChange.createdAt)], ['desc'])
    );

    // Возможно и такое что шаг в истории смены шагов может встречаться больше одного раза,
    // если заявление было отправлено на доработку, а для корректного определения прошло
    // (или лучше сказать находится) параллельные этапы нам необходимо смотреть историю переходов
    // и поскольку возможно такое что эти этапы были уже пройдены перед отправкой на доработку,
    //  то необходимо смотреть историю только после отправки на доработку
    // для этого ищем первый шаг в отсортированном по убыванию времени создания логе
    const lastCycleStartIndex = descendingLog.findIndex(
      (stepChange) => stepChange.stepId === firstStep.id
    );

    const lastCycleSteps = descendingLog.slice(0, lastCycleStartIndex + 1);
    // При переходе в этап, об этом создается запись в истории,
    // а при "уходе" из этапа, эта запись помечается как пройденная
    // так что необходимо просто проверить,
    // есть ли в истории запись с текущим шагом еще не пройденная
    if (this.head.siblings.length === 0) {
      // Если мы сейчас не на шаге который имеет параллельный шаг,
      // то достаточно посмотреть в историю не помечен ли текущий шаг как законченный
      return (
        !!lastCycleSteps.find(({ isEnded, stepId }) => this.head.id === stepId && !isEnded) || false
      );
    }
    // Если же у шага существуют "братья" (шаги, переходы в которых идут из одного родителя),
    // то в зависимости от типа маршрутизации переходов в братьев и в текущий шаг ...
    const single = this.head.siblings.reduce(
      // Поскольку ситуация, что из одного родителя могут быть переходы
      // разного типа маршрутизации не предусмотрена в рамках нашей логики
      // нам достаточно проверить наличие хотя бы одного перехода с типом 'single'
      (total, { routingType }) => total || routingType === 'single',
      false
    );

    const siblings = this.head.siblings.map(({ toStepId }) => toStepId);
    const passedSiblings = lastCycleSteps
      .filter(({ isEnded, stepId }) => siblings.includes(stepId) && isEnded)
      .map(({ stepId }) => stepId);

    if (single) {
      // для маршрутизации "один из" достаточно прохождения одного из братьев
      return passedSiblings.length === 0;
    }

    // Для параллельной же нужно что бы прошли через всех братьев
    return (
      passedSiblings.length < this.head.siblings.length && !passedSiblings.includes(this.head.id)
    );
  }

  protected get allBranchesTransitions(): Array<NIssue.FixedTransition> {
    return flatten(this.branches.map((step) => step.outgoingTransitions));
  }
}
